/* Kkumi Account hub — shared core.
 *
 * Exposes window.KKAcct: the App Proxy fetch helper, the memoised /dashboard
 * promise, DOM builders for the shared components (status chip, ledger row,
 * mini product row, tracking stepper, empty/error/skeleton states) and
 * formatting helpers. Also wires the shell header (tier badge + tab counts)
 * and the Idol holo-tilt effect.
 *
 * All rendering is DOM-API based — API data never goes through innerHTML.
 */
(function () {
  'use strict';
  if (window.KKAcct) return;

  var PROXY = '/apps/rewards';

  // Merchant copy overrides from theme settings (window.KKACCT_COPY, emitted
  // by snippets/kkumi-account-copy.liquid — inline, so it beats this deferred
  // script). Every string below falls back to the shipped default.
  var COPY = window.KKACCT_COPY || {};
  function copyVal(v, fallback) { return v == null || v === '' ? fallback : v; }

  function fetchProxy(path, opts) {
    opts = opts || {};
    return fetch(PROXY + path, {
      method: opts.method || 'GET',
      credentials: 'same-origin',
      headers: opts.body ? { 'content-type': 'application/json' } : undefined,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (json) {
        if (!res.ok) {
          var err = new Error(json.error || 'request_failed');
          err.status = res.status;
          err.payload = json;
          throw err;
        }
        return json;
      });
    });
  }

  var dashboardPromise = null;
  function getDashboard() {
    if (!dashboardPromise) {
      dashboardPromise = fetchProxy('/dashboard').catch(function (e) {
        dashboardPromise = null; // allow retry
        throw e;
      });
    }
    return dashboardPromise;
  }

  /* ---------- formatting ---------- */
  function money(cents, currency) {
    if (cents == null) return '';
    try {
      return new Intl.NumberFormat('en-AU', { style: 'currency', currency: currency || 'AUD' }).format(cents / 100);
    } catch (e) {
      return '$' + (cents / 100).toFixed(2);
    }
  }
  function fmtDate(iso) {
    var d = typeof iso === 'number' ? new Date(iso) : new Date(String(iso));
    if (isNaN(d)) return '';
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  function fmtDateTime(iso) {
    var d = new Date(String(iso));
    if (isNaN(d)) return '';
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) + ' · ' +
      d.toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' }).toLowerCase().replace(/\s/g, '');
  }
  function fmtPts(n) { return Number(n || 0).toLocaleString('en-AU'); }

  /* ---------- DOM helpers ---------- */
  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  /* ---------- status chip ---------- */
  var CHIP_LABELS = {
    processing: 'Processing',
    shipped: 'Packed',
    in_transit: 'On its way',
    out_for_delivery: 'Out for delivery',
    delivered: 'Delivered',
    cancelled: 'Cancelled',
    refunded: 'Refunded',
    attention: 'Needs attention',
  };
  Object.keys(CHIP_LABELS).forEach(function (k) {
    CHIP_LABELS[k] = copyVal((COPY.chips || {})[k], CHIP_LABELS[k]);
  });
  function chip(status) {
    var key = CHIP_LABELS[status] ? status : 'processing';
    return el('span', 'kka-chip kka-chip--' + key, CHIP_LABELS[key]);
  }

  /* ---------- Kkumi-voiced tracking line per stage ---------- */
  var VOICE = {
    ordered: 'Getting your pack ready.',
    packed: 'Packed & lodged, on the move soon.',
    in_transit: 'Your pack is on its way.',
    out_for_delivery: "Out for delivery. Today's the day.",
    delivered: 'Delivered. Happy collecting!',
    exception: 'Hmm, your pack hit a snag.',
  };
  Object.keys(VOICE).forEach(function (k) {
    VOICE[k] = copyVal((COPY.voice || {})[k], VOICE[k]);
  });
  function voiceFor(tracking) {
    if (!tracking) return VOICE.ordered;
    if (tracking.exception) return VOICE.exception;
    return VOICE[tracking.stage] || VOICE.ordered;
  }

  /* ---------- ledger row ---------- */
  function ledgerRow(row) {
    var li = el('li');
    var what = el('div', 'kka-ledger__what', row.label);
    what.appendChild(el('span', 'kka-ledger__when', fmtDate(row.date)));
    li.appendChild(what);
    var earn = Number(row.delta) >= 0;
    li.appendChild(el('span', 'kka-ledger__amt ' + (earn ? 'kka-ledger__amt--earn' : 'kka-ledger__amt--spend'),
      (earn ? '+' : '−') + fmtPts(Math.abs(row.delta)) + copyVal(COPY.pts_unit, ' pts')));
    return li;
  }

  /* ---------- product mini row ---------- */
  function miniProduct(item, currency) {
    var row = el('div', 'kka-mini');
    var img = el('img', 'kka-mini__thumb');
    img.alt = '';
    img.loading = 'lazy';
    if (item.image) img.src = item.image;
    row.appendChild(img);
    var body = el('div');
    body.style.minWidth = '0';
    body.appendChild(el('div', 'kka-mini__name', item.title || ''));
    var meta = [];
    if (item.quantity > 1) meta.push(copyVal(COPY.qty_prefix, 'qty ') + item.quantity);
    if (item.variant) meta.push(item.variant);
    if (meta.length) body.appendChild(el('div', 'kka-mini__meta', meta.join(' · ')));
    row.appendChild(body);
    if (item.price_cents != null) row.appendChild(el('span', 'kka-mini__price', money(item.price_cents, currency)));
    return row;
  }

  /* ---------- empty / error states ---------- */
  var ICONS = {
    heart: 'M19 14c1.5-1.5 3-3.3 3-5.5A4.5 4.5 0 0 0 17.5 4c-1.8 0-3 .7-4.1 2L12 7.5 10.6 6C9.5 4.7 8.3 4 6.5 4A4.5 4.5 0 0 0 2 8.5c0 2.2 1.5 4 3 5.5l7 7Z',
    box: 'M21 8 12 3 3 8v8l9 5 9-5Z M3 8l9 5 9-5 M12 13v8',
    spark: 'M12 2l2.4 7.6L22 12l-7.6 2.4L12 22l-2.4-7.6L2 12l7.6-2.4Z',
    cloud: 'M17.5 19a4.5 4.5 0 0 0 0-9 6 6 0 0 0-11.7 1.5A4 4 0 0 0 7 19Z M9.5 12.5 14.5 17.5 M14.5 12.5 9.5 17.5',
  };
  function svgIcon(name) {
    var ns = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(ns, 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.8');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');
    (ICONS[name] || ICONS.spark).split(' M').forEach(function (d, i) {
      var p = document.createElementNS(ns, 'path');
      p.setAttribute('d', (i ? 'M' : '') + d);
      svg.appendChild(p);
    });
    return svg;
  }
  function emptyState(opts) {
    var box = el('div', 'kka-empty');
    var icon = el('div', 'kka-empty__icon');
    icon.appendChild(svgIcon(opts.icon || 'spark'));
    box.appendChild(icon);
    var h = el('h3', 'kka-empty__title', (opts.title || '') + ' ');
    if (opts.pop) {
      h.appendChild(el('em', null, opts.pop));
      h.appendChild(document.createTextNode('.'));
    }
    box.appendChild(h);
    if (opts.copy) box.appendChild(el('p', 'kka-empty__copy', opts.copy));
    if (opts.ctaText && opts.ctaHref) {
      var a = el('a', 'button button--secondary button--small', opts.ctaText);
      a.href = opts.ctaHref;
      box.appendChild(a);
    }
    return box;
  }
  function errorState(what, onRetry) {
    var box = el('div', 'kka-error');
    var icon = el('div', 'kka-error__icon');
    icon.appendChild(svgIcon('cloud'));
    box.appendChild(icon);
    box.appendChild(el('h3', 'kka-error__title',
      copyVal(COPY.error_title, "Couldn't load {what}").replace(/\{what\}/g, what)));
    box.appendChild(el('p', 'kka-error__copy',
      copyVal(COPY.error_copy, 'Something hiccuped on our end; your {what} are safe. Give it another go.').replace(/\{what\}/g, what)));
    if (onRetry) {
      var btn = el('button', 'button button--secondary button--small', copyVal(COPY.error_retry, '↻ Try again'));
      btn.type = 'button';
      btn.addEventListener('click', onRetry);
      box.appendChild(btn);
    }
    return box;
  }

  /* ---------- skeletons ---------- */
  // Thin wrapper over the sitewide KKSkel twin (assets/kkumi-skeleton.js) so the
  // guest-track skeleton uses the same `.kk-skel` node as everything else.
  function skel(w, h, r) {
    return KKSkel.el({ tag: 'div', w: w == null ? '100%' : w, h: h || 14, radius: r });
  }
  // The account-page loading skeletons are server-rendered in Liquid (so
  // they're in the first paint, no deferred-script flash). This builder is for
  // the guest track page only, whose result skeleton appears after the lookup
  // form is submitted (JS-built by necessity). Mirrors the 4-step .kka-stp
  // tracking stepper (dot + label per step); the step's own padding-top:40px
  // makes room for the absolutely-positioned dot, so the label needs no margin.
  function skelStepper() {
    var wrap = el('div', 'kka-stp');
    for (var i = 0; i < 4; i++) {
      var step = el('div', 'kka-stp__step');
      var dot = KKSkel.el({ tag: 'div', w: 32, h: 32, radius: 999, style: 'position:absolute;top:0;left:50%;transform:translateX(-50%);' });
      step.appendChild(dot);
      var lbl = skel('70%', 11); lbl.style.margin = '0 auto'; step.appendChild(lbl);
      wrap.appendChild(step);
    }
    return wrap;
  }

  /* ---------- tracking stepper ---------- */
  var STEPS = [
    { key: 'packed', label: 'Packed & lodged', icon: 'box' },
    { key: 'in_transit', label: 'On its way', icon: 'truck' },
    { key: 'out_for_delivery', label: 'Out for delivery', icon: 'mailbox' },
    { key: 'delivered', label: 'Delivered', icon: 'party' },
  ];
  STEPS.forEach(function (st, i) { st.label = copyVal((COPY.steps || [])[i], st.label); });
  var STAGE_INDEX = { ordered: -1, packed: 0, in_transit: 1, out_for_delivery: 2, delivered: 3 };
  function stepper(tracking) {
    var current = STAGE_INDEX[tracking.stage];
    if (current == null) current = 0;
    var exception = !!tracking.exception && tracking.stage !== 'delivered';
    var wrap = el('div', 'kka-stp');
    STEPS.forEach(function (s, i) {
      var delivered = current === 3;
      var done = i < current || (delivered && i === 3);
      var now = i === current && !delivered;
      var cls = 'kka-stp__step' + (done ? ' is-done' : '') +
        (now ? (exception ? ' is-exc' : ' is-now') : '') +
        (!done && !now ? ' is-future' : '');
      var step = el('div', cls);
      var dot = el('span', 'kka-stp__dot');
      if (done) { dot.textContent = '✓'; }
      else if (now && exception) { dot.textContent = '!'; }
      else { dot.innerHTML = window.KKIcons ? window.KKIcons.html(s.icon) : ''; }
      step.appendChild(dot);
      var label = el('span', 'kka-stp__label', s.label);
      if (i === 3 && tracking.delivered_at) label.appendChild(el('span', 'kka-stp__when', fmtDate(tracking.delivered_at)));
      step.appendChild(label);
      wrap.appendChild(step);
    });
    return wrap;
  }

  /* ---------- full tracking card body (stepper + voice + events) ---------- */
  function trackingBody(tracking, opts) {
    opts = opts || {};
    var frag = document.createDocumentFragment();
    var voice = el('div', 'kka-voice');
    var voiceLine = el('p', 'kka-voice__line');
    if (window.KKIcons) {
      voiceLine.appendChild(window.KKIcons.el('sparkles', 'kka-voice__icon'));
      voiceLine.appendChild(document.createTextNode(' '));
    }
    voiceLine.appendChild(document.createTextNode(voiceFor(tracking)));
    voice.appendChild(voiceLine);
    if (tracking.eta && !tracking.exception && tracking.stage !== 'delivered') {
      voice.appendChild(el('span', 'kka-voice__eta', copyVal(COPY.eta_label, 'ETA ') + fmtDate(tracking.eta)));
    }
    frag.appendChild(voice);
    frag.appendChild(stepper(tracking));
    if (tracking.exception) {
      var exc = el('div', 'kka-exc');
      exc.appendChild(window.KKIcons ? window.KKIcons.el('alert-triangle', 'kka-exc__icon') : el('span', 'kka-exc__icon'));
      var body = el('div');
      body.appendChild(el('p', 'kka-exc__title', copyVal(COPY.exc_title, 'Delivery needs a hand')));
      var copy = el('p', 'kka-exc__copy',
        copyVal(COPY.exc_copy, "The carrier couldn't complete delivery; it may be waiting at your local post office. We'll email you the moment it's ready") +
        (opts.helpUrl ? ', or ' : '.'));
      if (opts.helpUrl) {
        var a = el('a', null, copyVal(COPY.exc_help_link, 'talk to us'));
        a.href = opts.helpUrl;
        copy.appendChild(a);
        copy.appendChild(document.createTextNode(copyVal(COPY.exc_copy_tail, " and we'll chase it.")));
      }
      body.appendChild(copy);
      exc.appendChild(body);
      frag.appendChild(exc);
    }
    frag.appendChild(trackingEvents(tracking));
    return frag;
  }

  /* ---------- carrier scan history (the tail of the tracking card) ---------- */
  // Split out of trackingBody so a refunded order can keep the scan history —
  // the pack may have shipped before the refund — without the stepper and
  // voice line that would imply it's still on its way.
  function trackingEvents(tracking) {
    var frag = document.createDocumentFragment();
    if (!tracking || !tracking.events || !tracking.events.length) return frag;
    var ev = el('div', 'kka-events');
    var head = el('div', 'kka-events__head');
    head.appendChild(el('h3', 'kka-events__title',
      (tracking.carrier || copyVal(COPY.carrier_fallback, 'Carrier')) + copyVal(COPY.scan_history_suffix, ' scan history')));
    if (tracking.number) {
      if (tracking.url) {
        var link = el('a', 'kka-card__link', tracking.number + ' ↗');
        link.href = tracking.url;
        link.target = '_blank';
        link.rel = 'noopener';
        head.appendChild(link);
      } else {
        head.appendChild(el('span', 'kka-events__title', tracking.number));
      }
    }
    ev.appendChild(head);
    var ul = el('ul');
    tracking.events.forEach(function (e) {
      var li = el('li');
      li.appendChild(el('time', null, fmtDateTime(e.at)));
      li.appendChild(el('span', null, e.description + (e.location ? ' · ' + e.location : '')));
      ul.appendChild(li);
    });
    ev.appendChild(ul);
    frag.appendChild(ev);
    return frag;
  }

  /* ---------- refund / cancellation notice ---------- */
  // Rendered from the `refund` block the API ships alongside `tracking` (see
  // refundSummary in apps/rewards/src/lib/order-status.js). A refunded or
  // cancelled order has no delivery left to look forward to, so callers show
  // this INSTEAD of the voice line + stepper rather than under them.
  var REFUND_TITLES = {
    refunded: 'Order refunded',
    partially_refunded: 'Partly refunded',
    cancelled: 'Order cancelled',
  };
  var REFUND_COPY = {
    refunded: '{amount} is on its way back to your original payment method. Banks usually take 3-5 business days to show it.',
    partially_refunded: "{amount} has been refunded to your original payment method. The rest of this order isn't affected.",
    cancelled: 'This order was cancelled, so nothing was packed and nothing shipped.',
  };
  var REFUND_META = {
    refunded: 'Refunded {date}',
    partially_refunded: 'Refunded {date}',
    cancelled: 'Cancelled {date}',
  };
  // Shopify's cancellation reasons, as customer-facing copy. The API sends the
  // token, never the raw enum.
  var REFUND_REASONS = {
    customer: 'You asked us to cancel this one.',
    declined: "The payment was declined, so the order didn't go through.",
    fraud: "We couldn't verify the payment, so we cancelled the order to be safe.",
    inventory: 'We ran out of stock before we could pack it.',
    staff: 'We cancelled this one from our end.',
    other: 'We cancelled this order.',
  };
  Object.keys(REFUND_TITLES).forEach(function (k) {
    REFUND_TITLES[k] = copyVal((COPY.refund_titles || {})[k], REFUND_TITLES[k]);
    REFUND_COPY[k] = copyVal((COPY.refund_state_copy || {})[k], REFUND_COPY[k]);
    REFUND_META[k] = copyVal((COPY.refund_meta || {})[k], REFUND_META[k]);
  });
  Object.keys(REFUND_REASONS).forEach(function (k) {
    REFUND_REASONS[k] = copyVal((COPY.refund_reasons || {})[k], REFUND_REASONS[k]);
  });

  function fillTokens(str, map) {
    return String(str == null ? '' : str).replace(/\{(\w+)\}/g, function (whole, key) {
      return map[key] == null ? '' : map[key];
    });
  }

  /**
   * Loud notice for a refunded / partly refunded / cancelled order.
   *   refund — the API's refund block ({ state, amount_cents, currency, at,
   *            cancel_reason, note }).
   *   opts.helpUrl — contact page; adds the "something not right?" line.
   */
  function refundNotice(refund, opts) {
    opts = opts || {};
    var state = REFUND_TITLES[refund.state] ? refund.state : 'refunded';
    // No amount to quote — either the order was cancelled before capture, or
    // the caller only knows the status chip. Either way the {amount} copy would
    // render a hole, so swap in the amount-free line.
    var noAmount = refund.amount_cents == null && state !== 'cancelled';

    var wrap = el('div', 'kka-refund');
    if (window.KKIcons) wrap.appendChild(window.KKIcons.el('info-fill', 'kka-refund__icon'));
    var body = el('div', 'kka-refund__body');
    body.appendChild(el('p', 'kka-refund__title', REFUND_TITLES[state]));
    body.appendChild(el('p', 'kka-refund__copy', noAmount
      ? copyVal(COPY.refund_copy_unknown, 'This order has been refunded to your original payment method.')
      : fillTokens(REFUND_COPY[state], { amount: money(refund.amount_cents, refund.currency) })));

    // The merchant's own words beat the generic reason when the shop opts in
    // to sending them (REFUND_NOTE_PUBLIC on the worker).
    var reason = refund.note || (refund.cancel_reason ? REFUND_REASONS[refund.cancel_reason] || REFUND_REASONS.other : '');
    if (reason) {
      var why = el('p', 'kka-refund__why');
      why.appendChild(el('span', 'kka-refund__why-label', copyVal(COPY.refund_reason_label, 'Why')));
      why.appendChild(document.createTextNode(reason));
      body.appendChild(why);
    }

    if (refund.at) {
      body.appendChild(el('p', 'kka-refund__meta', fillTokens(REFUND_META[state], { date: fmtDate(refund.at) })));
    }

    if (opts.helpUrl) {
      var help = el('p', 'kka-refund__help', copyVal(COPY.refund_help_copy, 'Not what you expected? '));
      var a = el('a', null, copyVal(COPY.refund_help_link, 'Talk to us'));
      a.href = opts.helpUrl;
      help.appendChild(a);
      body.appendChild(help);
    }
    wrap.appendChild(body);
    return wrap;
  }

  /* ---------- theme-editor sample data ---------- */
  // The editor preview is always an anonymous session (hosted customer
  // accounts can't sign in inside the editor iframe), so in design mode the
  // page drivers swap a not-logged-in/401 response for this sample payload —
  // merchants can style and edit copy for every signed-in state.
  var designMode = !!(window.Shopify && window.Shopify.designMode);

  function sampleDashboard() {
    var now = Date.now();
    var day = 86400000;
    var iso = function (t) { return new Date(t).toISOString(); };
    var inTransit = {
      source: 'shopify', number: 'AP123456789', url: null, carrier: 'Australia Post',
      stage: 'in_transit', exception: false, eta: iso(now + 2 * day), delivered_at: null,
    };
    var orders = [
      { id: 'sample-1', name: '#1042', created_at: iso(now - 2 * day), total_cents: 3900, currency: 'AUD',
        status_chip: 'in_transit', line_count: 3, thumbs: [null, null, null], points_earned: 49, tracking: inTransit },
      { id: 'sample-2', name: '#1037', created_at: iso(now - 20 * day), total_cents: 2400, currency: 'AUD',
        status_chip: 'delivered', line_count: 1, thumbs: [null], points_earned: 30,
        tracking: { source: 'shopify', number: 'AP987654321', url: null, carrier: 'Australia Post', stage: 'delivered', exception: false, eta: null, delivered_at: iso(now - 16 * day) } },

      { id: 'sample-3', name: '#1039', created_at: iso(now - 6 * day), total_cents: 5400, currency: 'AUD',
        status_chip: 'attention', line_count: 2, thumbs: [null, null], points_earned: 0,
        tracking: { source: 'shopify', number: 'AP555000111', url: null, carrier: 'Australia Post', stage: 'in_transit', exception: true, eta: null, delivered_at: null } },
    ];
    return {
      logged_in: true,
      sample: true,
      member: { first_name: 'Mia', name: 'Mia Park', email: 'mia@example.com', birthday: null, email_marketing: true },
      tier: { id: 'rookie', name: 'Rookie', multiplier: 1.25,
        progress: { next_tier_id: 'idol', next_tier_name: 'Idol', spend_cents: 20000, remaining_cents: 10000, pct: 66, tier_points: 200, next_tier_points: 300, is_top: false } },
      points: { balance: 320, lifetime: 980, dollar_value_cents: 1500, best_value_cents: 1500 },
      program: { earn_per_dollar: 1, redeem_step: 100, redeem_dollars: 5, birthday_bonus: 100, second_order_bonus: 1000,
        review_bonus: 50, review_media_bonus: 50, review_points_per_order: 300, referral_bonus: 100,
        referral_min_order_cents: 2000, streak_min_order_cents: 3000 },
      streak: { count: 2, days_left: 9, next_bonus: 100, capped: false },
      store_credit: { amount_cents: 1500, currency: 'AUD' },
      review_allowance: 300,
      reviewable: [
        { title: 'Blue Paradise Colour Pop Kit', handle: 'sample', image: null, order_name: '#1037', delivered_at: iso(now - 16 * day) },
        { title: 'Sour Grapes Colour Pop Kit', handle: 'sample', image: null, order_name: '#1041', delivered_at: null, shipped_at: iso(now - 9 * day) },
      ],
      referral: { code: 'SAMPLE', url: 'https://kkumi.com.au/?ref=SAMPLE', friends: [
        { name: 'Riley', state: 'earned', points: 100, at: now - 12 * day },
        { name: 'Ava', state: 'pending', points: 100, at: now - 3 * day },
      ] },
      open_codes: [
        { code: 'KR-SAMPLE', label: '$5 off', reward_type: 'fixed', expires_at: now + 6 * day },
        { code: 'KR-SHIP-DEMO', label: 'Free standard shipping', reward_type: 'free_shipping', expires_at: now + 9 * day },
      ],
      open_codes_count: 2,
      // Kkumi Oracle's on-site pick, so the editor shows the "Picked for you" card.
      oracle_pick: { arm: 'cross_sell', card: { heading: 'A perfect match', heading_parts: ['A perfect ', 'match', ''], line: 'What other collectors added next.',
        button: { label: 'Add it to my cart', url: '/collections/all' }, product: null },
        choices: { token: 'sample', product: true } },
      rewards: { affordable: 2, total: 4 },
      // The fan profile bonus still to earn, so the editor shows the Earn more · fan profile row.
      fandom_bonus: { state: 'available', points: 100 },
      orders_error: false,
      active_orders: [orders[0]],
      recent_orders: orders,
      history_preview: [
        { date: now - 2 * day, label: 'Order · $39.00', delta: 49 },
        { date: now - 20 * day, label: 'Order · $24.00', delta: 30 },
        { date: now - 30 * day, label: 'Joined the Kkumi Club', delta: 100 },
      ],
    };
  }

  function sampleOrderDetail() {
    var d = sampleDashboard();
    var o = d.recent_orders[0];
    var now = Date.now();
    var iso = function (t) { return new Date(t).toISOString(); };
    return Object.assign({}, o, {
      subtotal_cents: 3900,
      shipping_cents: 0,
      refunded_cents: 0,
      items: [
        { title: 'Colour Pop Kit · Strawberry', variant: null, quantity: 2, image: null, price_cents: 3000 },
        { title: 'Sticker Pack', variant: 'Mint', quantity: 1, image: null, price_cents: 900 },
      ],
      ship_to: { name: 'Mia Park', address1: '1 Pop Street', address2: null, city: 'Sydney', province: 'NSW', zip: '2000', country: 'Australia' },
      shipping_method: 'Standard shipping',
      tracking: Object.assign({}, o.tracking, {
        events: [
          { at: iso(now - 6 * 3600000), description: 'In transit', location: 'Chullora NSW' },
          { at: iso(now - 30 * 3600000), description: 'Shipment confirmed', location: 'Marrickville NSW' },
        ],
      }),
    });
  }

  /* ---------- Idol holo tilt ---------- */
  function bindHoloTilt(card) {
    if (!card || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    card.addEventListener('pointermove', function (e) {
      var r = card.getBoundingClientRect();
      var x = (e.clientX - r.left) / r.width;
      var y = (e.clientY - r.top) / r.height;
      card.style.setProperty('--hx', (x * 100).toFixed(1) + '%');
      card.style.setProperty('--hy', (y * 100).toFixed(1) + '%');
      card.style.setProperty('--hxn', x.toFixed(3));
      card.style.setProperty('--hr', ((x - 0.5) * 24).toFixed(1) + 'deg');
      card.style.setProperty('--rx', ((x - 0.5) * 6).toFixed(2) + 'deg');
      card.style.setProperty('--ry', ((0.5 - y) * 6).toFixed(2) + 'deg');
      card.classList.add('is-tilting');
    });
    card.addEventListener('pointerleave', function () {
      card.classList.remove('is-tilting');
      ['--hx', '--hy', '--hxn', '--hr', '--rx', '--ry'].forEach(function (v) { card.style.removeProperty(v); });
    });
  }

  /* ---------- tier card (member-card direction, shared) ---------- */
  // Tier glyphs render via the shared KKIcons SVG library (assets/kkumi-icons.js).
  var TIER_ICON_NAME = { trainee: 'seedling', rookie: 'star', idol: 'crown' };
  var TIER_KR = { trainee: '연습생', rookie: '신인', idol: '아이돌' };
  if (COPY.tier_kr && typeof COPY.tier_kr === 'object') {
    Object.keys(TIER_KR).forEach(function (k) { TIER_KR[k] = copyVal(COPY.tier_kr[k], TIER_KR[k]); });
  }
  function tierIconEl(tierId, cls) {
    return window.KKIcons
      ? window.KKIcons.el(TIER_ICON_NAME[tierId] || 'star', cls)
      : el('span', cls);
  }

  function earnRateLabel(d) {
    var p = d.program || {};
    var mult = Number(d.tier.multiplier) || 1;
    // Always the absolute rate ("15 pts per $1"), for every tier — a tier's
    // multiplier is not shown as a percentage.
    var rate = +(((p.earn_per_dollar || 1) * mult).toFixed(2));
    var tmpl = rate === 1
      ? copyVal(COPY.earn_rate_one, '{n} pt per $1')
      : copyVal(COPY.earn_rate_many, '{n} pts per $1');
    return tmpl.split('{n}').join(rate);
  }

  // Tier qualification is spend-based, but the customer-facing ladder is framed
  // in Polco Points (dollars × earn rate). The worker hands these point values
  // down on `progress` (tier_points / next_tier_points) so the rate is never
  // hardcoded here. "Tier Progress" counts purchases only — it intentionally
  // differs from the (purchases + bonuses) Available balance, hence the note.
  function tierProgressRow(t) {
    var p = t.progress || {};
    if (p.is_top) {
      var row = el('div', 'kka-idol-row');
      var flag = el('span', 'kka-idol-flag');
      flag.appendChild(tierIconEl('idol', 'kka-idol-flag__icon'));
      flag.appendChild(document.createTextNode(' ' + copyVal(COPY.idol_flag, 'Top tier, yours for good')));
      row.appendChild(flag);
      var stat = el('div', 'kka-idol-stat');
      stat.appendChild(el('b', null, fmtPts(p.tier_points || 0) + copyVal(COPY.pts_unit, ' pts')));
      stat.appendChild(el('span', null, copyVal(COPY.idol_lifetime, 'Polco Points from orders')));
      row.appendChild(stat);
      return row;
    }
    var toGo = Math.max(0, (p.next_tier_points || 0) - (p.tier_points || 0));
    var prog = el('div', 'kka-prog');
    var labels = el('div', 'kka-prog__labels');
    var next = el('span', 'kka-prog__next', copyVal(COPY.prog_next_label, 'Next: '));
    if (p.next_tier_id) next.appendChild(tierIconEl(p.next_tier_id, 'kka-prog__next-icon'));
    next.appendChild(document.createTextNode(' ' + (p.next_tier_name || '')));
    labels.appendChild(next);
    labels.appendChild(el('span', 'kka-prog__togo',
      copyVal(COPY.prog_togo_label, '{n} Polco Points to go').split('{n}').join(fmtPts(toGo))));
    prog.appendChild(labels);
    var track = el('div', 'kka-prog__track');
    var fill = el('div', 'kka-prog__fill');
    fill.style.width = Math.max(3, p.pct || 0) + '%';
    track.appendChild(fill);
    prog.appendChild(track);
    prog.appendChild(el('div', 'kka-prog__note',
      copyVal(COPY.prog_note, 'Tier Progress: {a} / {b} Polco Points · {clarifier}')
        .split('{a}').join(fmtPts(p.tier_points || 0))
        .split('{b}').join(fmtPts(p.next_tier_points || 0))
        .split('{clarifier}').join(copyVal(COPY.tier_clarifier, 'Only points earned from orders count toward your tier.'))));
    return prog;
  }

  /** Build the member tier card. opts: brand, brandKr, memberName, memberNo. */
  function tierCard(d, opts) {
    opts = opts || {};
    var t = d.tier;
    var card = el('div', 'kka-tiercard kka-tmat--' + t.id);
    card.appendChild(el('span', 'kka-tiercard__kr', TIER_KR[t.id] || ''));

    var top = el('div', 'kka-tiercard__top');
    var brand = el('div', 'kka-tiercard__brand', opts.brand || 'The Kkumi Club');
    if (opts.brandKr) brand.appendChild(el('small', null, opts.brandKr));
    top.appendChild(brand);
    var badge = el('span', 'kka-tbadge kka-tbadge--' + t.id);
    badge.appendChild(tierIconEl(t.id, 'kka-tbadge__icon'));
    badge.appendChild(document.createTextNode(t.name));
    top.appendChild(badge);
    card.appendChild(top);

    var mid = el('div', 'kka-tiercard__mid');
    var pts = el('div');
    pts.appendChild(el('div', 'kka-tiercard__label', copyVal(COPY.points_label, 'Polco Points')));
    var big = el('div', 'kka-tiercard__points', fmtPts(d.points.balance) + ' ');
    big.appendChild(el('span', 'kka-tiercard__pts-unit', copyVal(COPY.tiercard_pts_unit, 'pts')));
    pts.appendChild(big);
    pts.appendChild(el('div', 'kka-tiercard__value',
      copyVal(COPY.tiercard_value, '= {value} off your next order · {rate}')
        .split('{value}').join(money(d.points.dollar_value_cents))
        .split('{rate}').join(earnRateLabel(d))));
    mid.appendChild(pts);
    var midIcon = el('div', 'kka-tiercard__icon');
    midIcon.appendChild(tierIconEl(t.id, 'kka-tiercard__icon-svg'));
    mid.appendChild(midIcon);
    card.appendChild(mid);

    var bottom = el('div', 'kka-tiercard__bottom');
    var who = el('div', 'kka-tiercard__who');
    who.appendChild(el('span', 'kka-tiercard__name', d.member.name || opts.memberName || ''));
    who.appendChild(el('span', 'kka-tiercard__memno', opts.memberNo || ''));
    bottom.appendChild(who);
    bottom.appendChild(tierProgressRow(t));
    card.appendChild(bottom);

    if (t.id === 'idol') bindHoloTilt(card);
    return card;
  }

  /* ---------- standalone tier-card section driver ---------- */
  function hydrateTierCard(root) {
    getDashboard()
      .then(function (d) {
        if (!d.logged_in) d = designMode ? sampleDashboard() : null;
        if (!d) return;
        root.replaceChildren(tierCard(d, {
          brand: root.dataset.copyBrand,
          brandKr: root.dataset.copyBrandKr,
          memberName: root.dataset.memberName,
          memberNo: root.dataset.memberNo,
        }));
      })
      .catch(function () {
        if (designMode) {
          root.replaceChildren(tierCard(sampleDashboard(), {
            brand: root.dataset.copyBrand, brandKr: root.dataset.copyBrandKr,
          }));
        }
        // Live failure: keep the server-rendered skeleton — the card is
        // decorative context here, not the page's primary content.
      });
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-kka-tiercard]').forEach(function (root) {
      if (root.dataset.kkBound) return;
      root.dataset.kkBound = '1';
      hydrateTierCard(root);
    });
  });

  /* ---------- shell header hydration ---------- */
  // True when this page is being viewed as someone else's shared wishlist
  // (?shared=<publicId> from the wishlist share copy). The account chrome
  // is owner-shaped ("HEY JAY.", tier badge, member number, account tabs,
  // sign-out), which is the wrong frame for a shared view — so we hide it
  // and reveal the [data-kka-shared] banner instead. KkwlBus.sharedId is
  // pre-populated from the URL in kkumi-wishlist-bus.js.
  function isSharedView() {
    try {
      if (window.KkwlBus && window.KkwlBus.sharedId) return true;
      return !!new URLSearchParams(window.location.search).get('shared');
    } catch (e) { return false; }
  }

  function applySharedHeader() {
    // Hide owner chrome + the signed-out gate (a shared list shouldn't ask
    // the viewer to log in to see it).
    document.querySelectorAll('[data-kka-owner-chrome], [data-kka-guest-gate]').forEach(function (el) {
      el.hidden = true;
    });
    document.querySelectorAll('[data-kka-shared]').forEach(function (el) {
      el.hidden = false;
    });
    // Subscribe for the owner name once the grid resolves the public list.
    function setName(name) {
      name = (name || '').trim();
      document.querySelectorAll('[data-kka-shared-name]').forEach(function (el) {
        // Fallback to the merchant-editable server-rendered name (the
        // section's "Shared owner name fallback" setting) so the empty-name
        // case stays in sync with the Liquid default.
        var fallback = (el.dataset.fallbackName || el.textContent || 'a friend').trim() || 'a friend';
        if (!el.dataset.fallbackName) el.dataset.fallbackName = fallback;
        el.textContent = name ? name : fallback;
      });
    }
    // Seed from ?by= (carried in the share URL) so we don't flash "a friend"
    // before the public-list endpoint resolves.
    try {
      var by = new URLSearchParams(window.location.search).get('by');
      if (by) setName(by);
    } catch (e) {}
    if (window.KkwlBus && window.KkwlBus.on) {
      window.KkwlBus.on('shared', function (d) { if (d && d.name) setName(d.name); });
    }
  }

  /* ---------- "For you" split (shared by the header badge + the tab) ----------
   * Facts only — the For you tab owns the copy. Needs action = order problems
   * (delivery exceptions). Deadlines = time-sensitive things due within 14
   * days: the streak window closing and claimed codes about to expire. The
   * header badge counts both; optional prompts are never counted. */
  var DEADLINE_DAYS = 14;
  function forYouSplit(d) {
    var now = Date.now();
    var dayMs = 86400000;
    var action = (d.recent_orders || []).filter(function (o) {
      return o.status_chip === 'attention' || (o.tracking && o.tracking.exception);
    });
    var deadlines = [];
    if (d.streak && d.streak.days_left <= DEADLINE_DAYS) {
      deadlines.push({ kind: 'streak', at: now + d.streak.days_left * dayMs, days: d.streak.days_left });
    }
    (d.open_codes || []).forEach(function (c) {
      if (!c.expires_at) return;
      var days = Math.ceil((c.expires_at - now) / dayMs);
      if (days >= 0 && days <= DEADLINE_DAYS) deadlines.push({ kind: 'code', at: c.expires_at, days: days, code: c });
    });
    deadlines.sort(function (x, y) { return x.at - y.at; });
    return { action: action, deadlines: deadlines, count: action.length + deadlines.length };
  }

  function setMetric(root, key, parts) {
    var m = root.querySelector('[data-kka-m="' + key + '"]');
    if (!m) return;
    var v = m.querySelector('[data-kka-m-v]');
    var sub = m.querySelector('[data-kka-m-s]');
    var l = m.querySelector('[data-kka-m-l]');
    if (l && parts.label != null) l.textContent = parts.label;
    if (v) v.replaceChildren.apply(v, parts.value);
    if (sub) {
      if (parts.sub == null) sub.remove();
      else sub.replaceChildren.apply(sub, [].concat(parts.sub));
    }
    if (parts.aria) m.setAttribute('aria-label', parts.aria);
  }

  function renderMetrics(root, d) {
    var t = d.tier || {};
    var p = d.points || {};
    // "Worth up to {value} off" quotes the biggest money-off reward the member
    // can claim right now (worker: points.best_value_cents), never a raw
    // conversion of the balance. Hidden when nothing money-off is claimable.
    // A worker older than the theme has no best_value_cents: fall back to the
    // balance's redeem value.
    var best = p.best_value_cents != null ? p.best_value_cents : p.dollar_value_cents;
    setMetric(root, 'points', {
      value: [document.createTextNode(fmtPts(p.balance))],
      sub: best > 0 ? copyVal(COPY.m_points_sub, 'Worth up to {value} off').split('{value}').join(window.KKMoney ? window.KKMoney.fromCents(best, { trim: true, shop: true }) : money(best)) : null,
    });
    var sc = d.store_credit;
    var credit = sc && sc.currency ? money(sc.amount_cents, sc.currency) : (sc ? money(0) : '—');
    setMetric(root, 'credit', {
      value: [document.createTextNode(credit)],
      sub: sc && sc.amount_cents > 0
        ? copyVal(COPY.m_credit_sub, 'Use at checkout')
        : (sc ? copyVal(COPY.m_credit_empty, 'None yet') : null),
    });
    var prog = t.progress || {};
    if (prog.is_top || !prog.next_tier_name) {
      setMetric(root, 'tier', {
        label: copyVal(COPY.m_tier_top_label, 'Your tier'),
        value: [tierIconEl(t.id, 'kka-m__icon'), document.createTextNode(t.name || '')],
        sub: earnRateLabel(d),
      });
    } else {
      // Tier progress counts points earned from orders (tier_points), the same
      // framing as the tier card; bonuses don't move it.
      var togo = fmtPts(Math.max(0, (prog.next_tier_points || 0) - (prog.tier_points || 0)));
      var pct = prog.next_tier_points ? (prog.tier_points || 0) / prog.next_tier_points * 100 : (prog.pct || 0);
      var bar = el('span', 'kka-m__bar');
      var fill = el('span');
      fill.style.width = Math.max(3, Math.min(100, pct)) + '%';
      bar.appendChild(fill);
      setMetric(root, 'tier', {
        label: copyVal(COPY.m_tier_label, 'Points to {tier}').split('{tier}').join(prog.next_tier_name),
        value: [document.createTextNode(togo)],
        sub: bar,
        aria: copyVal(COPY.m_tier_aria, '{value} points to earn until {tier}')
          .split('{value}').join(togo).split('{tier}').join(prog.next_tier_name),
      });
    }
  }

  function renderForYouCount(d) {
    var n = forYouSplit(d).count;
    var tip = copyVal(COPY.foryou_badge_aria, '{n} updates: order issues and deadlines in the next 14 days')
      .split('{n}').join(n);
    document.querySelectorAll('[data-kka-count="foryou"]').forEach(function (node) {
      if (n > 0) {
        node.textContent = String(n);
        node.title = tip;
        node.setAttribute('aria-label', tip);
        node.hidden = false;
      } else node.hidden = true;
    });
  }

  function hydrateHeader(root) {
    if (isSharedView()) { applySharedHeader(); return; }
    var badge = root.querySelector('[data-kka-badge]');
    // One memoised /dashboard fetch feeds the badge, the balances strip and
    // the For you count on every account page (the dashboard tab reuses it).
    var source = getDashboard().then(function (d) {
      return d && d.logged_in ? d : (designMode ? sampleDashboard() : null);
    }).catch(function (e) {
      if (designMode) return sampleDashboard();
      throw e;
    });
    var dropSkel = function () {
      var sk = root.querySelector('[data-kka-badge-skel]');
      if (sk) sk.remove();
    };
    source.then(function (d) {
      dropSkel();
      if (!d) return;
      var tier = d.tier;
      if (badge && tier) {
        badge.className = 'kka-tbadge kka-tbadge--' + tier.id;
        badge.replaceChildren(
          tierIconEl(tier.id, 'kka-tbadge__icon'),
          document.createTextNode(tier.name),
        );
        badge.hidden = false;
      }
      renderMetrics(root, d);
      renderForYouCount(d);
    }).catch(function () {
      dropSkel();
      // Figures unknown: a dash beats a shimmer that never resolves.
      ['points', 'credit', 'tier'].forEach(function (k) {
        setMetric(root, k, {
          label: k === 'tier' ? copyVal(COPY.m_tier_top_label, 'Your tier') : null,
          value: [document.createTextNode('—')], sub: null,
        });
      });
    });
  }

  // Wishlist tab count rides the wishlist bus. Registered at script top level
  // (not DOMContentLoaded) — kkumi-wishlist-core.js emits its cached count in
  // its own DOMContentLoaded handler, which runs before ours would bind.
  document.addEventListener('wk:countUpdated', function (e) {
    // Trust only our worker's events — the embedded swish-wishlist-king @app
    // block re-fires `wk:countUpdated` from its own stale state on heart
    // clicks, which would flash this WISHLIST tab badge.
    if (!(e && e.detail && e.detail.source === 'kkwl')) return;
    var n = Number(e.detail.count) || 0;
    document.querySelectorAll('[data-kka-count="wishlist"]').forEach(function (node) {
      if (n > 0) { node.textContent = String(n); node.hidden = false; }
      else node.hidden = true;
    });
  });

  /* ---------- sticky tab bar ---------- */
  // The tab bar sticks under the site navbar. The navbar is position: fixed
  // once scrolled (kkncl-fixed), so the offset is its height while fixed and
  // 0 otherwise; written to --kka-stick-top on <html>. The section wrapper is
  // display: contents (kkumi-account-shell.css) so the bar can stick past it.
  function stickTabbar() {
    if (!document.querySelector('.kka-tabbar')) return;
    var queued = false;
    var sync = function () {
      queued = false;
      var nav = document.querySelector('.kkncl') || document.querySelector('.section-header');
      var top = 0;
      if (nav) {
        var r = nav.getBoundingClientRect();
        var pos = window.getComputedStyle(nav).position;
        if (pos === 'fixed' || pos === 'sticky') top = Math.max(0, Math.round(r.bottom));
      }
      document.documentElement.style.setProperty('--kka-stick-top', top + 'px');
    };
    var queue = function () { if (!queued) { queued = true; window.requestAnimationFrame(sync); } };
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
    sync();
  }

  document.addEventListener('DOMContentLoaded', function () {
    stickTabbar();
    // Shared-view swap runs unconditionally — the data-kka-header element
    // doesn't exist for guests (the section only renders the signed-out gate
    // in that branch), but a guest visiting `?shared=<id>` still needs the
    // owner chrome / login gate replaced with the shared banner.
    if (isSharedView()) { applySharedHeader(); }
    document.querySelectorAll('[data-kka-header]').forEach(function (root) {
      if (root.dataset.kkBound) return;
      root.dataset.kkBound = '1';
      hydrateHeader(root);
    });
  });

  window.KKAcct = {
    fetchProxy: fetchProxy,
    getDashboard: getDashboard,
    money: money,
    fmtDate: fmtDate,
    fmtDateTime: fmtDateTime,
    fmtPts: fmtPts,
    el: el,
    chip: chip,
    chipLabel: function (s) { return CHIP_LABELS[s] || CHIP_LABELS.processing; },
    voiceFor: voiceFor,
    ledgerRow: ledgerRow,
    miniProduct: miniProduct,
    emptyState: emptyState,
    errorState: errorState,
    skel: skel,
    skelStepper: skelStepper,
    stepper: stepper,
    trackingBody: trackingBody,
    trackingEvents: trackingEvents,
    refundNotice: refundNotice,
    bindHoloTilt: bindHoloTilt,
    designMode: designMode,
    sampleDashboard: sampleDashboard,
    sampleOrderDetail: sampleOrderDetail,
    tierCard: tierCard,
    earnRateLabel: earnRateLabel,
    forYouSplit: forYouSplit,
    tierIconEl: tierIconEl,
    copy: function (key, fallback) { return copyVal(COPY[key], fallback); },
  };
})();
